import React from 'react';
import { Instagram, Youtube, Facebook, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const Footer = () => (
  <footer id="contact" data-testid="footer-section" style={{
    background: 'var(--bg-surface)',
    borderTop: '1px solid var(--border)',
    padding: '4rem 0 2rem',
  }}>
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 1.5rem" }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '3rem',
        marginBottom: '3rem',
      }}>
        {/* Logo & Beschreibung */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <img src="/images/trxl-shop-logo.png" alt="TrXL Performance" style={{ height: '36px', objectFit: 'contain' }} />
          </div>
          <p style={{ color: 'var(--fg-muted)', fontSize: '0.85rem', lineHeight: 1.6 }}>
            Dein Partner für Chiptuning, Softwareoptimierung und Performance-Teile.
            Über 30.000 Produkte von Top-Marken.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            {[
              { icon: Instagram, href: 'https://www.instagram.com/trxl_performance/' },
              { icon: Youtube, href: 'https://www.youtube.com/@trxlperformance6136' },
              { icon: Facebook, href: 'https://www.facebook.com/trxl.performance/' },
            ].map(({ icon: Icon, href }) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                style={{
                  width: '36px', height: '36px', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', border: '1px solid var(--border)', color: 'var(--fg-muted)',
                  transition: 'all 0.3s', textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--fg-muted)'; }}
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        {/* Shop Links */}
        <div>
          <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>Shop</h4>
          {[
            { label: 'Alle Produkte', href: '/shop' },
            { label: 'Tuning nach Fahrzeug', href: '/tuning' },
            { label: 'Fahrwerke Übersicht', href: '/fahrwerke' },
            { label: 'Gewindefahrwerke', href: '/fahrwerke/typ/gewindefahrwerk' },
            { label: 'Sportfahrwerke', href: '/fahrwerke/typ/sportfahrwerk' },
            { label: 'Tieferlegungsfedern', href: '/fahrwerke/typ/tieferlegungsfedern' },
            { label: 'Ratgeber & Magazin', href: '/ratgeber' },
            { label: 'Marken-Vergleiche', href: '/vergleich' },
            { label: 'Sportauspuffe', href: '/shop?q=auspuff' },
            { label: 'Spoiler & Bodykits', href: '/shop?q=spoiler' },
            { label: 'Ladeluftkühler', href: '/shop?q=ladeluftkuehler' },
          ].map(l => (
            <a key={l.label} href={l.href} style={{
              display: 'block', color: 'var(--fg-muted)', fontSize: '0.85rem', textDecoration: 'none',
              padding: '0.3rem 0', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.target.style.color = 'white'}
              onMouseLeave={e => e.target.style.color = 'var(--fg-muted)'}
            >{l.label}</a>
          ))}
        </div>

        {/* Marken */}
        <div>
          <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>Top-Marken</h4>
          {[
            { label: 'Maxton Design', href: '/marke/maxton-design' },
            { label: 'CSR Automotive', href: '/marke/csr-automotive' },
            { label: 'GRAIL Abgasanlagen', href: '/marke/grail-automotive-gmbh' },
            { label: 'Rieger Tuning', href: '/marke/rieger' },
            { label: 'Wagner Tuning', href: '/marke/wagner-tuning-gmbh-co-kg' },
            { label: 'KW Fahrwerke', href: '/fahrwerke/marke/kw' },
            { label: 'Bilstein Fahrwerke', href: '/fahrwerke/marke/bilstein' },
            { label: 'Eibach Federn', href: '/fahrwerke/marke/eibach' },
            { label: 'ST Suspensions', href: '/fahrwerke/marke/st-suspensions' },
            { label: 'H&R Federn', href: '/fahrwerke/marke/h-r' },
            { label: 'Alle Marken', href: '/marken' },
          ].map(l => (
            <a key={l.label} href={l.href} style={{
              display: 'block', color: 'var(--fg-muted)', fontSize: '0.85rem', textDecoration: 'none',
              padding: '0.3rem 0', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.target.style.color = 'white'}
              onMouseLeave={e => e.target.style.color = 'var(--fg-muted)'}
            >{l.label}</a>
          ))}
        </div>

        {/* Services */}
        <div>
          <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>Services</h4>
          {[
            { label: 'Chiptuning', href: 'https://chiptuning.trxl-performance.de/' },
            { label: 'Fileservice Portal', href: 'https://fileservice.trxl-performance.de/' },
            { label: 'Angebot anfordern', href: 'https://www.trxl-konfigurator.de/anfrage/anfrage.html' },
          ].map(l => (
            <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" style={{
              display: 'block', color: 'var(--fg-muted)', fontSize: '0.85rem', textDecoration: 'none',
              padding: '0.3rem 0', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.target.style.color = 'white'}
              onMouseLeave={e => e.target.style.color = 'var(--fg-muted)'}
            >{l.label}</a>
          ))}
        </div>

        {/* Kontakt */}
        <div>
          <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>Kontakt</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <a href="tel:+491725498156" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--fg-muted)', fontSize: '0.85rem', textDecoration: 'none' }}>
              <Phone size={14} color="var(--primary)" /> 0172 5498156
            </a>
            <a href="https://wa.me/491725498156" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--fg-muted)', fontSize: '0.85rem', textDecoration: 'none' }}>
              <MessageCircle size={14} color="var(--primary)" /> WhatsApp
            </a>
            <a href="mailto:anfrage@trxl-performance.de" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--fg-muted)', fontSize: '0.85rem', textDecoration: 'none' }}>
              <Mail size={14} color="var(--primary)" /> anfrage@trxl-performance.de
            </a>
            <span style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: 'var(--fg-muted)', fontSize: '0.85rem' }}>
              <MapPin size={14} color="var(--primary)" style={{ marginTop: '3px', flexShrink: 0 }} />
              <span>
                Schilfweg 5, 28857 Syke<br />
                Werkstatt: Dr. Neidhard Str. 8, 27305 Bruchhausen-Vilsen
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Payment & Bottom */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)' }}>
          &copy; {new Date().getFullYear()} TrXL Performance. Alle Rechte vorbehalten.
        </p>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <a href="/vertrag-widerrufen" data-testid="footer-widerruf-button" style={{
            fontSize: '0.72rem', fontWeight: 700, color: 'var(--primary)', textDecoration: 'none',
            border: '1px solid var(--primary)', borderRadius: '6px', padding: '0.35rem 0.8rem',
            transition: 'all 0.2s', letterSpacing: '0.02em',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--primary)'; }}
          >Vertrag widerrufen</a>
          {[
            { label: 'Gewährleistung', href: '/rechtliches/gewaehrleistung' },
            { label: 'Impressum', href: '/impressum' },
            { label: 'Datenschutz', href: '/datenschutz' },
            { label: 'AGB', href: '/agb' },
            { label: 'Versand & Zahlung', href: '/versand' },
            { label: 'Widerruf', href: '/widerruf' },
            { label: 'Cookie-Richtlinie', href: '/cookie-richtlinie' },
            { label: 'Gewinnspiel', href: '/gewinnspiel' },
            { label: 'Teilnahmebedingungen', href: '/teilnahmebedingungen-gewinnspiel' },
          ].map(l => (
            <a key={l.label} href={l.href} style={{ fontSize: '0.7rem', color: 'var(--fg-muted)', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = 'white'}
              onMouseLeave={e => e.target.style.color = 'var(--fg-muted)'}
            >{l.label}</a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
